// @/services/analytics/posthog.js

/**
 * PostHog Analytics Service
 * 
 * GDPR-compliant analytics service following PostHog's latest React documentation.
 * Only initializes and tracks when user has explicitly consented via cookie banner.
 * 
 * Features:
 * - Consent-based initialization following PostHog React patterns
 * - Dynamic opt-in/opt-out with proper cleanup
 * - User identification with project context
 * - Custom event tracking for platform actions
 * - Automatic pageview tracking
 * - Feature flag support (when needed)
 * - Debug mode for development
 * - Proper error handling and fallbacks
 * 
 * Based on PostHog React documentation:
 * https://posthog.com/docs/libraries/react
 */

import { authService } from '@/services/auth';

class PostHogAnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.consentGiven = false;
    this.posthog = null;

    // Environment-based configuration
    // Each environment (production, staging, demo) uses its own PostHog project
    // via GATSBY_POSTHOG_API_KEY set in Netlify env vars per deploy context.
    // Development (localhost) is disabled — no key = no tracking.
    this.config = {
      apiKey: process.env.GATSBY_POSTHOG_API_KEY || '',
      // Reverse-proxied via Netlify (see netlify.toml [[redirects]] for /r-3kf8a/*).
      // Browser calls a same-origin path with a project-specific token; Netlify
      // forwards to PostHog's EU edge. Path is intentionally non-obvious to dodge
      // tracking-protection filter lists that pattern-match well-known proxy
      // paths like `/ingest/*`.
      apiHost: '/r-3kf8a',
      uiHost: process.env.GATSBY_POSTHOG_HOST || 'https://eu.i.posthog.com',

      options: {
        debug: false,

        // Capture settings
        capture_pageview: true,
        capture_pageleave: true,
        capture_performance: true,

        opt_out_capturing_by_default: false,
        respect_dnt: true,

        // Session and persistence — localStorage only (no cookies).
        // Avoids the `dmn_chk_*` cookie rejection that fires when PostHog's
        // server-side Set-Cookie response carries a Domain attribute that
        // doesn't match the page hostname (which happens even through the
        // reverse proxy because PostHog itself doesn't know about the proxy).
        // Tracking continuity is preserved because PostHog falls back to
        // localStorage for the distinct ID + session state.
        persistence: 'localStorage',

        // Session timeout
        session_timeout_ms: 30 * 60 * 1000,

        // Feature flags
        feature_flag_request_timeout_ms: 3000,

        // Person profiles
        person_profiles: 'identified_only',

        // Session recording enabled for user research
        disable_session_recording: false,

        // SESSION RECORDING CONFIGURATION
        session_recording: {
          sample_rate: 0.25,
          record_console: false,
          record_network: true,
          opt_in: true,
        },

        capture_heatmaps: true,

        // API version
        defaults: '2025-05-24',

        // Callback for debugging
        loaded: function (posthog) {
          if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
            window.posthog = posthog;
          }
        }
      }
    };

    // Bind methods to preserve context
    this.handleConsentChange = this.handleConsentChange.bind(this);
    this.trackEvent = this.trackEvent.bind(this);
    this.identifyUser = this.identifyUser.bind(this);
    this.trackPageView = this.trackPageView.bind(this);

    this.debugLog('PostHog Analytics Service created');
  }

  /**
   * Initialize PostHog analytics service
   * Checks for existing consent and sets up event listeners
   */
  initialize() {
    // Idempotent: a second call (provider remount) would register the consent
    // listener again and init posthog-js twice, loading its extensions twice.
    if (!this.initPromise) this.initPromise = this.runInitialize();
    return this.initPromise;
  }

  async runInitialize() {
    try {
      // Skip entirely on localhost and when no API key is configured
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        this.debugLog('PostHog disabled on localhost');
        return false;
      }

      if (!this.config.apiKey) {
        this.debugLog('PostHog disabled — no API key configured for this environment');
        return false;
      }

      this.debugLog('Initializing PostHog Analytics Service...');

      // Check for existing consent
      const existingConsent = localStorage.getItem('analyticsConsent');
      this.consentGiven = existingConsent === 'true';

      this.debugLog(`Existing consent: ${this.consentGiven}`);

      // Set up consent change listener
      window.addEventListener('analyticsConsentChanged', this.handleConsentChange);

      // Initialize PostHog if consent already given
      if (this.consentGiven) {
        await this.initializePostHog();
      } else if (existingConsent === 'false') {
        // User previously declined - send their status once per session
        // Use sessionStorage to prevent firing on every page load
        const alreadyTrackedThisSession = sessionStorage.getItem('posthog_rejection_tracked');
        if (!alreadyTrackedThisSession) {
          await this.trackConsentRejection('existing_preference');
          sessionStorage.setItem('posthog_rejection_tracked', 'true');
        }
      }

      this.debugLog('PostHog Analytics Service initialized');
      return true;
    } catch (error) {
      console.error('[PostHog] Service initialization error:', error);
      return false;
    }
  }

  /**
   * Handle consent changes from cookie banner or settings
   * @param {CustomEvent} event - Consent change event
   */
  async handleConsentChange(event) {
    const { enabled, source } = event.detail;
    this.debugLog(`Consent changed: ${enabled} (source: ${source})`);

    this.consentGiven = enabled;

    if (enabled && !this.isInitialized) {
      // User gave consent - initialize PostHog
      await this.initializePostHog();
    } else if (!enabled && this.isInitialized) {
      // User revoked consent - disable PostHog
      this.disablePostHog();
      // Track the rejection from settings (user had previously consented)
      await this.trackConsentRejection(source || 'settings');
    } else if (!enabled && !this.isInitialized) {
      // User rejected consent (from banner or settings) - track rejection
      await this.trackConsentRejection(source || 'banner');
    }
  }

  /**
   * Track consent rejection without enabling full analytics
   * This sends a single event to PostHog when user declines analytics,
   * then immediately opts out and cleans up.
   *
   * Purpose: Understand what percentage of active users decline analytics
   *
   * PRIVACY: Only sends anonymous, aggregate data - no PII whatsoever
   * @param {string} source - Where the rejection came from ('banner' or 'settings')
   * @private
   */
  async trackConsentRejection(source = 'banner') {
    try {
      this.debugLog('Tracking consent rejection...');

      // Dynamically import PostHog library
      const { default: posthog } = await import('posthog-js');

      // Check if user is logged in (boolean only - no identifying info)
      const authState = authService.getAuthState();
      const isLoggedIn = !!(authState?.authenticated && authState.user);

      // Initialize PostHog minimally just to send this one event
      // Use random anonymous ID - no persistence, no identification
      posthog.init(this.config.apiKey, {
        api_host: this.config.apiHost,
        ui_host: this.config.uiHost,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        persistence: 'memory', // Don't persist anything
        bootstrap: { distinctID: `anon_${Date.now()}_${Math.random().toString(36).slice(2)}` }
      });

      // Send ONLY anonymous, aggregate data - no PII
      posthog.capture('analytics_consent_declined', {
        source: source,
        is_logged_in: isLoggedIn
      });

      this.debugLog('Consent rejection event sent (anonymous)');

      // Immediately opt out and reset to respect user's choice
      // Small delay to ensure event is sent before cleanup
      setTimeout(() => {
        posthog.opt_out_capturing();
        posthog.reset();
        this.debugLog('PostHog cleaned up after rejection tracking');
      }, 500);

    } catch (error) {
      // Silent fail - don't break the app if this fails
      console.error('[PostHog] Failed to track consent rejection:', error);
    }
  }

  /**
   * Initialize PostHog library following React documentation patterns
   * @private
   */
  async initializePostHog() {
    try {
      this.debugLog('Loading PostHog library...');

      // Dynamically import PostHog library (following React docs)
      const { default: posthog } = await import('posthog-js');

      // Initialize PostHog with configuration (following React docs pattern)
      posthog.init(this.config.apiKey, {
        api_host: this.config.apiHost,
        ui_host: this.config.uiHost,
        ...this.config.options
      });

      // Opt into capturing (since we default to opt-out)
      posthog.opt_in_capturing();

      // Tag every event with the current environment for filtering
      const environment = this.getEnvironment();
      posthog.register({ environment });

      this.posthog = posthog;
      this.isInitialized = true;

      this.debugLog(`PostHog initialized for environment: ${environment}`);

      // Identify user if authenticated
      await this.identifyUser();

      // Track initialization event
      this.trackEvent('analytics_enabled', {
        source: 'service_initialization',
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
        page_url: window.location.href
      });

      return true;
    } catch (error) {
      console.error('[PostHog] Failed to initialize PostHog:', error);
      this.isInitialized = false;
      return false;
    }
  }

  /**
   * Disable PostHog tracking and clear data (following docs)
   * @private
   */
  disablePostHog() {
    if (!this.posthog || !this.isInitialized) return;

    try {
      this.debugLog('Disabling PostHog tracking...');

      // Track opt-out event before disabling
      this.trackEvent('analytics_disabled', {
        source: 'user_opt_out',
        timestamp: new Date().toISOString()
      });

      // Opt out of capturing (following docs)
      this.posthog.opt_out_capturing();

      // Clear stored data (following docs)
      this.posthog.reset();

      this.isInitialized = false;
      this.debugLog('PostHog tracking disabled and data cleared');
    } catch (error) {
      console.error('[PostHog] Error disabling PostHog:', error);
    }
  }

  /**
   * Identify the current user with PostHog (following React docs pattern)
   * @private
   */
  async identifyUser() {
    if (!this.posthog || !this.isInitialized) return;

    try {
      const authState = authService.getAuthState();

      if (authState?.authenticated && authState.user) {
        const user = authState.user;
        const projectId = authService.getProjectId();

        // Create user identifier (following docs recommendation)
        const userId = user.email || `project_${projectId}_user`;

        // Identify user with properties (following React docs pattern)
        this.posthog.identify(userId, {
          email: user.email,
          name: user.name,
          project_id: projectId,
          is_main_user: user.isMainUser || false,
          job_title: user.jobTitle,
          phone_number: user.phoneNumber,
          first_login: user.firstLogin || new Date().toISOString(),
          language: navigator.language || 'en',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });

        this.debugLog(`User identified: ${userId}`);

        // Set project as group (following docs)
        if (projectId) {
          this.posthog.group('project', projectId, {
            project_id: projectId,
            created_at: user.projectCreatedAt || new Date().toISOString(),
            plan_type: 'standard' // You can update this with actual plan info
          });
        }
      }
    } catch (error) {
      console.error('[PostHog] User identification error:', error);
    }
  }

  /**
   * Track custom events (following React docs pattern)
   * @param {string} eventName - Name of the event
   * @param {Object} properties - Event properties
   * @param {Object} options - PostHog options
   */
  trackEvent(eventName, properties = {}, options = {}) {
    if (!this.posthog || !this.isInitialized || !this.consentGiven) {
      this.debugLog(`Event tracking skipped (not initialized or no consent): ${eventName}`);
      return;
    }

    try {
      // Add common properties to all events
      const enhancedProperties = {
        ...properties,
        $current_url: window.location.href,
        $pathname: window.location.pathname,
        $search: window.location.search,
        timestamp: new Date().toISOString(),
        session_id: this.getSessionId(),
        platform: 'web',
        app_version: process.env.GATSBY_APP_VERSION || '1.0.0'
      };

      // Capture event (following docs)
      this.posthog.capture(eventName, enhancedProperties, options);

      this.debugLog(`Event tracked: ${eventName}`, enhancedProperties);
    } catch (error) {
      console.error('[PostHog] Event tracking error:', error);
    }
  }

  /**
   * Track page views manually (useful for SPAs)
   * @param {string} url - Page URL (can be relative or absolute)
   * @param {Object} properties - Additional properties
   */
  trackPageView(url = window.location.href, properties = {}) {
    if (!this.posthog || !this.isInitialized || !this.consentGiven) return;

    try {
      // Handle relative URLs by converting to absolute
      let fullUrl;
      try {
        // Try to create URL object - if it fails, it's a relative path
        fullUrl = new URL(url);
      } catch (e) {
        // It's a relative path, make it absolute
        fullUrl = new URL(url, window.location.origin);
      }

      // Enhanced page view properties
      const pageProperties = {
        ...properties,
        $current_url: fullUrl.href,
        $pathname: fullUrl.pathname,
        $search: fullUrl.search,
        $title: document.title,
        $referrer: document.referrer || null,
        timestamp: new Date().toISOString()
      };

      this.posthog.capture('$pageview', pageProperties);
      this.debugLog(`Page view tracked: ${fullUrl.href}`);
    } catch (error) {
      console.error('[PostHog] Page view tracking error:', error);
    }
  }

  /**
   * Track feature usage events
   * @param {string} feature - Feature name
   * @param {string} action - Action taken
   * @param {Object} properties - Additional properties
   */
  trackFeatureUsage(feature, action, properties = {}) {
    this.trackEvent('feature_used', {
      feature_name: feature,
      action: action,
      ...properties
    });
  }

  /**
   * Track user engagement events
   * @param {string} type - Engagement type
   * @param {Object} properties - Additional properties
   */
  trackEngagement(type, properties = {}) {
    this.trackEvent('user_engagement', {
      engagement_type: type,
      ...properties
    });
  }

  /**
   * Determine current environment from hostname
   * @returns {string} 'production' | 'staging' | 'demo' | 'development'
   * @private
   */
  getEnvironment() {
    if (typeof window === 'undefined') return 'unknown';
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'development';
    if (hostname.includes('staging')) return 'staging';
    if (hostname.includes('demo')) return 'demo';
    return 'production';
  }

  /**
   * Get session ID for consistent tracking
   * @private
   */
  getSessionId() {
    if (this.posthog && this.posthog.get_session_id) {
      return this.posthog.get_session_id();
    }
    return null;
  }

  /**
   * Get PostHog instance (for advanced usage)
   * @returns {Object|null} PostHog instance or null
   */
  getPostHogInstance() {
    return this.posthog;
  }

  /**
   * Check if PostHog is initialized and consent given
   * @returns {boolean} True if ready for tracking
   */
  isReady() {
    return this.isInitialized && this.consentGiven;
  }

  /**
   * Debug logging helper
   * Only logs important events, filters out noise
   * @private
   */
  debugLog(message, data = null) {
    if (process.env.NODE_ENV === 'development') {
      // Skip verbose event tracking logs
      const skipPatterns = [
        'Event tracked:',
        'Page view tracked:'
      ];
      
      if (skipPatterns.some(pattern => message.includes(pattern))) {
        return; // Silent skip
      }
      
      // Log important lifecycle events only
      if (data) {
        console.log(`[PostHog] ${message}`, data);
      } else {
        console.log(`[PostHog] ${message}`);
      }
    }
  }

  /**
   * Cleanup method for service shutdown
   */
  destroy() {
    try {
      window.removeEventListener('analyticsConsentChanged', this.handleConsentChange);

      if (this.posthog && this.isInitialized) {
        this.posthog.opt_out_capturing();
        this.posthog.reset();
      }

      this.isInitialized = false;
      this.consentGiven = false;
      this.posthog = null;
      this.initPromise = null;

      this.debugLog('PostHog service destroyed');
    } catch (error) {
      console.error('[PostHog] Cleanup error:', error);
    }
  }
}

// Create and export singleton instance
export const postHogService = new PostHogAnalyticsService();

// Default export for convenience
export default postHogService;